import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import type { DashboardUser } from "@xop/dashboard-shared";
import { useEffect, useState } from "react";
import type { JSX } from "react";

import { AvatarUploader } from "../../../components/AvatarUploader.js";
import { ApiError } from "../../../lib/api.js";

export function RenameUserDialog({
  user,
  onClose,
  onSubmit,
}: {
  user: DashboardUser | null;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
}): JSX.Element {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (user) {
      setName(user.name);
      setError(null);
      setBusy(false);
    }
  }, [user]);

  async function submit(): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(trimmed);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not rename the user.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={user !== null} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Rename user</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: 1 }}
          onKeyDown={(e) => {
            if (e.key === "Enter") void submit();
          }}
        />
        {error && (
          <Typography sx={{ mt: 1, fontSize: 12, color: "error.main" }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={busy || !name.trim()}>
          {busy ? "Saving…" : "Save name"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function SetPasswordDialog({
  user,
  onClose,
  onSubmit,
}: {
  user: DashboardUser | null;
  onClose: () => void;
  onSubmit: (password: string) => Promise<void>;
}): JSX.Element {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (user) {
      setPw("");
      setConfirm("");
      setError(null);
      setBusy(false);
      setDone(false);
    }
  }, [user]);

  async function submit(): Promise<void> {
    if (pw.length < 12) {
      setError("Use at least 12 characters.");
      return;
    }
    if (pw !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit(pw);
      setDone(true);
      setTimeout(onClose, 1200);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not set the password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={user !== null} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Set password{user ? ` for ${user.name}` : ""}</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2 }}>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>
          Sets a new password for this user immediately. They&rsquo;ll need to sign in again
          with it (their current sessions are invalidated).
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          type="password"
          label="New password"
          autoComplete="new-password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
        />
        <TextField
          fullWidth
          size="small"
          type="password"
          label="Confirm password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        {error && <Typography sx={{ fontSize: 12, color: "error.main" }}>{error}</Typography>}
        {done && (
          <Typography sx={{ fontSize: 12, color: "success.main" }}>Password updated.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={busy || done}>
          {busy ? "Setting…" : "Set password"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function SetAvatarDialog({
  user,
  onClose,
}: {
  user: DashboardUser | null;
  onClose: () => void;
}): JSX.Element {
  return (
    <Dialog open={user !== null} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Set photo{user ? ` for ${user.name}` : ""}</DialogTitle>
      <DialogContent>
        {user && (
          <Box sx={{ pt: 1 }}>
            <AvatarUploader
              userId={user.id}
              name={user.name}
              endpoint={`/api/admin/users/${user.id}/avatar`}
              size={96}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
